import {
  base64ToBytes,
  type DeclaredAgeRange,
  type NetworkStatus,
  type PlatformAdapter,
  type PlatformCapability,
  type PushPayload,
  type ShareContent,
  type ShareImage,
  type ShareImageOutcome,
  type ShareOutcome,
} from "./types";
import { webAudioSession } from "./soundSession";

export class WebPlatformAdapter implements PlatformAdapter {
  private micStream: MediaStream | null = null;

  async queryMicPermission(): Promise<PermissionState> {
    try {
      const result = await navigator.permissions.query({ name: "microphone" as PermissionName });
      return result.state;
    } catch {
      // No Permissions API for the microphone (older Firefox). Answer "prompt"
      // rather than probing with getUserMedia, which would show the browser's
      // prompt before our context screen.
      return "prompt";
    }
  }

  async getMicStream(): Promise<MediaStream> {
    this.micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    return this.micStream;
  }

  releaseMicStream(): void {
    this.micStream?.getTracks().forEach((t) => t.stop());
    this.micStream = null;
  }

  async saveLocal(key: string, data: Uint8Array): Promise<void> {
    const db = await this.openDB();
    const tx = db.transaction("blobs", "readwrite");
    tx.objectStore("blobs").put(data, key);
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async loadLocal(key: string): Promise<Uint8Array | null> {
    const db = await this.openDB();
    const tx = db.transaction("blobs", "readonly");
    const req = tx.objectStore("blobs").get(key);
    return new Promise((resolve, reject) => {
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => reject(req.error);
    });
  }

  async deleteLocal(key: string): Promise<void> {
    const db = await this.openDB();
    const tx = db.transaction("blobs", "readwrite");
    tx.objectStore("blobs").delete(key);
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async registerPushToken(): Promise<string> {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return "";
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({ userVisibleOnly: true });
    return JSON.stringify(sub.toJSON());
  }

  onPushReceived(handler: (payload: PushPayload) => void): void {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", (e) => {
        if (e.data?.type === "push") handler(e.data.payload);
      });
    }
  }

  getPlatform(): "web" { return "web"; }

  getScreenSize() {
    return { width: window.innerWidth, height: window.innerHeight };
  }

  isCapabilityAvailable(cap: PlatformCapability): boolean {
    switch (cap) {
      case "microphone": return !!navigator.mediaDevices?.getUserMedia;
      case "push-notifications": return "PushManager" in window;
      case "file-storage": return "indexedDB" in window;
      case "camera": return !!navigator.mediaDevices?.getUserMedia;
    }
  }

  isOnline(): boolean {
    return typeof navigator === "undefined" ? true : navigator.onLine !== false;
  }

  onNetworkChange(handler: (status: NetworkStatus) => void): () => void {
    if (typeof window === "undefined") return () => {};
    const online = () => handler({ online: true });
    const offline = () => handler({ online: false });
    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    return () => {
      window.removeEventListener("online", online);
      window.removeEventListener("offline", offline);
    };
  }

  async getDeclaredAgeRange(): Promise<DeclaredAgeRange> {
    // The web has no platform age signal. `null` = "not stated", never "adult".
    return null;
  }

  // The browser manages its own audio session, except that project sound
  // asks for "playback" to play through the ringer switch (soundSession.ts).
  async activateVoiceAudio(): Promise<void> {
    await webAudioSession.activateVoice();
  }

  async deactivateVoiceAudio(): Promise<void> {
    await webAudioSession.deactivateVoice();
  }

  async requestSoundPlayback(): Promise<void> {
    await webAudioSession.requestSound();
  }

  async releaseSoundPlayback(): Promise<void> {
    await webAudioSession.releaseSound();
  }

  private openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open("lwim-storage", 1);
      req.onupgradeneeded = () => req.result.createObjectStore("blobs");
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  async share(_content: ShareContent): Promise<ShareOutcome> {
    // Task 16.1 scopes the share sheet to native; the web panel copies the link.
    return "unavailable";
  }

  canSaveToPhotos(): boolean {
    return false;
  }

  async shareImage(image: ShareImage, prefer: "share" | "download"): Promise<ShareImageOutcome> {
    let blob: Blob;
    try {
      // base64ToBytes allocates its own buffer, exactly the picture's length.
      blob = new Blob([base64ToBytes(image.base64).buffer as ArrayBuffer], { type: image.mime });
    } catch {
      return "unavailable";
    }
    // The share sheet only where a person expects it (a touch device) and only when it
    // takes files; a laptop gets the file in its Downloads, which is what it expects.
    const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
    if (prefer === "share" && typeof nav.share === "function" && typeof File === "function") {
      const file = new File([blob], image.name, { type: image.mime });
      let can = false;
      try { can = !!nav.canShare?.({ files: [file] }); } catch { can = false; }
      if (can) {
        try {
          await nav.share({ files: [file] });
          return "shared";
        } catch (err) {
          if ((err as { name?: string } | null)?.name === "AbortError") return "cancelled";
          // NotAllowedError (the gesture expired) and the rest: download instead.
        }
      }
    }
    try {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = image.name;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
      return "downloaded";
    } catch {
      return "unavailable";
    }
  }

  onAppUrlOpen(_handler: (path: string) => void): () => void {
    // The browser already navigated to the link.
    return () => {};
  }

  async hapticConfirm(): Promise<void> {
    // Native only (R16.3).
  }

  async getAppVersion(): Promise<null> {
    // The web app deploys with the backend; there is no installed version.
    return null;
  }

  async setKeepAwake(_on: boolean): Promise<void> {
    // Native only (R7.6). The browser keeps its own screen timeout.
  }
}
